import { EProgrammingStatus } from "../../models/programming";
import {
    plannedVsActualDeltaMinutes,
    plannedVsNowDeltaMinutes,
} from "../../utils/programming-timeline-utils";

export type TorreStatusGroup =
    | 'aguardando'
    | 'emExecucao'
    | 'atrasadas'
    | 'finalizadas'
    | 'canceladas'
    | 'todos';

export const STATUS_LABELS: Record<string, string> = {
    Waiting: 'Aguardando início',
    Created: 'Criada',
    Loading: 'Em carregamento',
    LoadedTransit: 'Em trânsito carregado',
    Unloading: 'Em descarga',
    Completed: 'Concluída',
    Finished: 'Finalizada',
    Cancelled: 'Cancelada',
    InProgress: 'Em progresso',
    Available: 'Disponível',
    EmptyTransit: 'Em trânsito vazio',
    DelayedDeparture: 'Atraso na saída',
    DelayedLoading: 'Atraso no carregamento',
    DelayedTransit: 'Atraso em trânsito',
    DelayedUnloading: 'Atraso na descarga',
    DelayedArrival: 'Atraso na chegada'
};

export const STATUS_COLORS: Record<string, string> = {
    Available: '#9b59b6',
    EmptyTransit: '#2ecc71',
    Loading: '#f1c40f',
    LoadedTransit: '#e67e22',
    Unloading: '#e74c3c',
    InProgress: '#3498db',
    DelayedTransit: '#e74c3c',
    DelayedLoading: '#e74c3c',
    DelayedUnloading: '#e74c3c',
    DelayedDeparture: '#e74c3c',
    DelayedArrival: '#e74c3c',
    Completed: '#27ae60',
    Finished: '#16a085',
    Cancelled: '#95a5a6',
    Waiting: '#3498db',
    Created: '#1f7ed6'
};

export function normalizeProgrammingStatus(status: string | number): number | null {
    if (typeof status === "number") return status;
    if (!isNaN(Number(status))) return Number(status);
    if (status in EProgrammingStatus) return EProgrammingStatus[status as keyof typeof EProgrammingStatus];
    return null;
}

export function toStatusKey(status: string | number): string {
    if (typeof status === "string" && isNaN(Number(status)) && status in EProgrammingStatus) return status;
    const normalized = normalizeProgrammingStatus(status);
    if (normalized === null) return String(status);
    return EProgrammingStatus[normalized] || String(status);
}

export function isStatusInGroup(status: string | number, group: TorreStatusGroup): boolean {
    const statusValue = normalizeProgrammingStatus(status);
    if (statusValue === null) return group === 'todos';

    switch (group) {
        case 'aguardando':
            return [EProgrammingStatus.Waiting, EProgrammingStatus.Created, EProgrammingStatus.Available].includes(statusValue);
        case 'emExecucao':
            return [EProgrammingStatus.Loading, EProgrammingStatus.LoadedTransit, EProgrammingStatus.Unloading, EProgrammingStatus.InProgress, EProgrammingStatus.EmptyTransit].includes(statusValue);
        case 'atrasadas':
            return [EProgrammingStatus.DelayedDeparture, EProgrammingStatus.DelayedLoading, EProgrammingStatus.DelayedTransit, EProgrammingStatus.DelayedUnloading, EProgrammingStatus.DelayedArrival].includes(statusValue);
        case 'finalizadas':
            return [EProgrammingStatus.Completed, EProgrammingStatus.Finished].includes(statusValue);
        case 'canceladas':
            return [EProgrammingStatus.Cancelled].includes(statusValue);
        case 'todos':
        default:
            return true;
    }
}

export function getTrackingStatusColor(status: string | number): string {
    const statusKey = toStatusKey(status);
    return STATUS_COLORS[statusKey] || '#95a5a6';
}

export function getTrackingStatusLabel(status: string | number): string {
    const statusKey = toStatusKey(status);
    return STATUS_LABELS[statusKey] || String(statusKey);
}

/** Recentraliza o mapa de tracking só quando o id selecionado muda (não a cada poll). */
export function shouldRecenterTrackingFocus(
    previousId: string | null | undefined,
    nextId: string | null | undefined
): boolean {
    if (nextId == null || nextId === '') return false;
    return previousId !== nextId;
}

/** Só zera o ref de foco se a seleção foi limpa — DTO ausente no poll não conta. */
export function shouldResetTrackingFocusRef(selectedProgrammingId?: string | null): boolean {
    return selectedProgrammingId == null || selectedProgrammingId === '';
}

/** Overview Brasil só quando não há coords e também não há veículo selecionado. */
export function shouldFallbackToBrazilOverview(
    hasValidLocations: boolean,
    selectedProgrammingId?: string | null
): boolean {
    return !hasValidLocations && (selectedProgrammingId == null || selectedProgrammingId === '');
}

/** ETA curto para modal/mapa — contrato estável: "Atrasado" sem minutos. */
export function formatEstimatedArrival(expectedArrivalTime?: string, nowDate: Date = new Date()): string {
    if (!expectedArrivalTime) return '-';

    const sincePlanned = plannedVsNowDeltaMinutes(expectedArrivalTime, nowDate);
    if (sincePlanned === null) return '-';

    const minutesUntil = -sincePlanned;
    if (minutesUntil <= 0) return 'Atrasado';
    if (minutesUntil < 60) return `${minutesUntil} min`;

    const hours = Math.floor(minutesUntil / 60);
    const minutes = minutesUntil % 60;
    return `${hours}h ${minutes}min`;
}

export type ScheduleDeviationTone = 'late' | 'early' | 'onTime' | 'unknown';

export interface ScheduleDeviation {
    text: string;
    tone: ScheduleDeviationTone;
    minutesDiff: number | null;
}

/** ETA vs agora: negativo = atrasado; positivo grande = folga (no prazo / adiantado em relacao ao ETA). */
export function formatScheduleDeviation(
    expectedArrivalTime?: string,
    nowDate: Date = new Date()
): ScheduleDeviation {
    if (!expectedArrivalTime) {
        return { text: '-', tone: 'unknown', minutesDiff: null };
    }

    const sincePlanned = plannedVsNowDeltaMinutes(expectedArrivalTime, nowDate);
    if (sincePlanned === null) {
        return { text: '-', tone: 'unknown', minutesDiff: null };
    }

    const minutesDiff = -sincePlanned;

    if (minutesDiff <= 0) {
        const late = Math.abs(minutesDiff);
        return {
            text: late === 0 ? 'Atrasado' : `Atrasado ${late} min`,
            tone: 'late',
            minutesDiff,
        };
    }

    if (minutesDiff >= 60) {
        const hours = Math.floor(minutesDiff / 60);
        const minutes = minutesDiff % 60;
        return {
            text: `${hours}h ${minutes}min`,
            tone: 'onTime',
            minutesDiff,
        };
    }

    return {
        text: `${minutesDiff} min`,
        tone: 'onTime',
        minutesDiff,
    };
}

export function formatActualScheduleDeviation(
    expectedArrivalTime?: string,
    actualArrivalTime?: string
): ScheduleDeviation | null {
    if (!expectedArrivalTime || !actualArrivalTime) return null;

    const actualMinusPlanned = plannedVsActualDeltaMinutes(expectedArrivalTime, actualArrivalTime);
    if (actualMinusPlanned === null) return null;

    const minutesDiff = -actualMinusPlanned;

    if (minutesDiff >= 15) {
        return {
            text: `Adiantado ${minutesDiff} min`,
            tone: 'early',
            minutesDiff,
        };
    }
    if (minutesDiff <= -15) {
        return {
            text: `Atrasado ${Math.abs(minutesDiff)} min`,
            tone: 'late',
            minutesDiff,
        };
    }
    return {
        text: 'No prazo',
        tone: 'onTime',
        minutesDiff,
    };
}

export function getProgrammingScheduleDisplay(programming: {
    expectedDestinationArrivalAt?: string;
    expectedArrivalTime?: string;
    actualDestinationEnteredAt?: string;
    status?: string | number;
}): ScheduleDeviation {
    const expected = programming.expectedDestinationArrivalAt ?? programming.expectedArrivalTime;
    const actual = programming.actualDestinationEnteredAt;
    const actualDeviation = formatActualScheduleDeviation(expected, actual);
    if (actualDeviation) return actualDeviation;

    // Concluída/cancelada sem chegada real: não comparar o prazo com o relógio de agora
    // (evita "Atrasado NNNNN min" em viagens já encerradas).
    if (programming.status !== undefined
        && (isStatusInGroup(programming.status, 'finalizadas') || isStatusInGroup(programming.status, 'canceladas'))) {
        return { text: '-', tone: 'unknown', minutesDiff: null };
    }

    return formatScheduleDeviation(expected);
}

export function isDelayedStatus(status: string | number): boolean {
    return isStatusInGroup(status, 'atrasadas');
}

export function getStatusLabelColor(status: string | number): 'red' | 'green' | 'grey' | 'orange' | 'blue' | 'teal' | 'yellow' {
    const key = toStatusKey(status);
    if (key.startsWith('Delayed')) return 'red';
    if (key === 'Completed' || key === 'Finished') return 'green';
    if (key === 'Cancelled') return 'grey';
    if (key === 'LoadedTransit' || key === 'Loading') return 'orange';
    if (key === 'Available' || key === 'Created' || key === 'Waiting') return 'blue';
    if (key === 'InProgress' || key === 'EmptyTransit') return 'teal';
    return 'grey';
}


function isLikelyBrazilLatLng(lat: number, lng: number): boolean {
    return lat >= -34 && lat <= 5 && lng >= -74 && lng <= -34;
}

/** Corrige lat/lng invertidos vindos de dados legados (ex.: Betim lat~-19, lng~-44). */
export function normalizeTrackingCoordinates(
    lat?: number,
    lng?: number
): { lat: number; lng: number } | null {
    if (lat === undefined || lng === undefined) return null;

    const nLat = Number(lat);
    const nLng = Number(lng);
    if (Number.isNaN(nLat) || Number.isNaN(nLng)) return null;
    if (nLat === 0 && nLng === 0) return null;

    if (!isLikelyBrazilLatLng(nLat, nLng) && isLikelyBrazilLatLng(nLng, nLat)) {
        return { lat: nLng, lng: nLat };
    }

    return { lat: nLat, lng: nLng };
}
